import Phaser from 'phaser';
import { ITEM_DEFINITIONS, SLOT_LABELS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { InventoryManager } from '../systems/InventoryManager';
import { RARITY_COLORS } from './InventoryUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { FONT_BODY } from '../utils/Typography';

// === MODALE DE DESTRUCTION D'ITEM ===
// Composant UI réutilisable affichant l'inventaire (sac OU consommables) avec
// un bouton [Jeter] par item nécessitant un double-clic de confirmation.
//
// Utilisé typiquement quand le joueur tente de prendre un loot mais n'a plus
// de place — la modale s'ouvre, il jette ce qu'il veut, puis le pickup
// originel se rejoue automatiquement via le callback `onAfterDiscard`.

export type DiscardCategory = 'transport' | 'consumables';

export interface DiscardModalOptions {
  category: DiscardCategory;
  /** Appelé après suppression effective d'un item (typiquement pour re-tenter le pickup). */
  onAfterDiscard?: () => void;
  /** Appelé si l'utilisateur ferme la modale sans rien jeter. */
  onCancel?: () => void;
}

export class DiscardItemModal {
  private elements: Phaser.GameObjects.GameObject[] = [];
  private confirmingUid: string | null = null;
  private confirmTimer: Phaser.Time.TimerEvent | null = null;

  constructor(
    private scene: Phaser.Scene,
    private inv: InventoryManager,
    private opts: DiscardModalOptions,
  ) {}

  show(): void {
    this.cleanup();
    this.draw();
  }

  /** Détruit tous les éléments visuels. Idempotent. */
  destroy(): void {
    this.cleanup();
  }

  private draw(): void {
    const overlay = this.scene.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.85)
      .setDepth(200).setInteractive();
    this.elements.push(overlay);

    const isTransport = this.opts.category === 'transport';
    const titleEmoji = isTransport ? '🎒' : '🧪';
    const titleText = isTransport
      ? `Sac plein (${this.inv.getTransportCount()}/${this.inv.getTransportCapacity()}) — faire de la place`
      : `Consommables max (${this.inv.getConsumables().length}/${this.inv.getMaxConsumables()}) — faire de la place`;

    const title = this.scene.add.text(GAME_CX, 70, `${titleEmoji} ${titleText}`, {
      fontSize: '14px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      align: 'center', wordWrap: { width: GAME_W - 40 },
    }).setOrigin(0.5).setDepth(201);
    this.elements.push(title);

    const subtitle = this.scene.add.text(GAME_CX, 100, 'Choisis un item à jeter (cliquer 2× pour confirmer)', {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(201);
    this.elements.push(subtitle);

    let y = 130;
    const items = isTransport ? this.inv.getTransport() : this.inv.getConsumables();

    if (items.length === 0) {
      const empty = this.scene.add.text(GAME_CX, y + 40, '(rien à jeter — déjà vide)', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(201);
      this.elements.push(empty);
    } else {
      for (const item of items) {
        y = this.drawItemRow(item.uid, item.definitionId, y);
      }
    }

    // Bouton annuler en bas
    const cancelBtn = this.scene.add.text(GAME_CX, GAME_H - 60, '[ Annuler ]', {
      fontSize: '13px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 14, y: 6 },
    }).setOrigin(0.5).setDepth(201);
    cancelBtn.setInteractive({ useHandCursor: true });
    cancelBtn.on('pointerdown', () => {
      this.cleanup();
      this.opts.onCancel?.();
    });
    cancelBtn.on('pointerover', () => cancelBtn.setColor('#ff6b6b'));
    cancelBtn.on('pointerout', () => cancelBtn.setColor('#e74c3c'));
    this.elements.push(cancelBtn);
  }

  private drawItemRow(uid: string, definitionId: string, y: number): number {
    const isTransport = this.opts.category === 'transport';
    const def = isTransport ? ITEM_DEFINITIONS[definitionId] : CONSUMABLE_DEFINITIONS[definitionId];
    if (!def) return y;
    const color = RARITY_COLORS[def.rarity] ?? '#ffffff';
    const colorNum = parseInt(color.replace('#', ''), 16);
    const rowW = Math.min(440, GAME_W - 30);
    const lx = Math.round(GAME_CX - rowW / 2 + 8);
    const rx = Math.round(GAME_CX + rowW / 2 - 8);
    const slotLabel = isTransport ? (SLOT_LABELS[(def as any).slot] ?? (def as any).slot) : 'Conso';

    const bg = this.scene.add.rectangle(GAME_CX, y + 17, rowW, 38, 0x1e2d4a)
      .setStrokeStyle(1, colorNum).setDepth(201);
    this.elements.push(bg);

    const label = this.scene.add.text(lx, y + 4, `${def.emoji} ${def.name}`, {
      fontSize: '11px', color, fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setDepth(202);
    this.elements.push(label);

    const slotTxt = this.scene.add.text(lx, y + 20, `[${slotLabel}]`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setDepth(202);
    this.elements.push(slotTxt);

    const dropBtn = this.scene.add.text(rx, y + 9, '[Jeter]', {
      fontSize: '11px', color: '#e67e22', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 6, y: 3 },
    }).setOrigin(1, 0).setDepth(202);
    dropBtn.setInteractive({ useHandCursor: true });
    dropBtn.on('pointerdown', () => this.handleDropClick(uid, dropBtn));
    this.elements.push(dropBtn);

    return y + 42;
  }

  private handleDropClick(uid: string, btn: Phaser.GameObjects.Text): void {
    if (this.confirmingUid === uid) {
      // 2e clic sur le même item → confirmation
      const removed = this.opts.category === 'transport'
        ? this.inv.removeFromTransport(uid) !== null
        : this.inv.discardConsumable(uid);
      this.cleanup();
      if (removed) this.opts.onAfterDiscard?.();
      return;
    }
    // 1er clic → marquer pour confirmation
    this.confirmingUid = uid;
    btn.setText('⚠ Confirmer ?');
    btn.setStyle({ color: '#ff6b6b' });
    if (this.confirmTimer) this.confirmTimer.remove();
    this.confirmTimer = this.scene.time.delayedCall(3000, () => {
      if (btn.active) {
        btn.setText('[Jeter]');
        btn.setStyle({ color: '#e67e22' });
      }
      this.confirmingUid = null;
      this.confirmTimer = null;
    });
  }

  private cleanup(): void {
    if (this.confirmTimer) {
      this.confirmTimer.remove();
      this.confirmTimer = null;
    }
    for (const e of this.elements) {
      if (e.active) e.destroy();
    }
    this.elements = [];
    this.confirmingUid = null;
  }
}
